<template>
  <div class="sp-tab-group">
    <div ref="tabsContainer" class="sp-tab-group__nav" role="tablist" :aria-label="ariaLabel">
      <slot name="nav" @slotchange="refreshSlots" />
    </div>
    <div ref="panelsContainer" class="sp-tab-group__panels">
      <slot @slotchange="refreshSlots" />
    </div>
  </div>
</template>

<script setup>
import { useEventListener, useMutationObserver } from "@vueuse/core";
import { onMounted, shallowRef, useTemplateRef, watch } from "vue";

import { useAriaLabel } from "#ce/composables/useAriaLabel";
import { useHostElement } from "#ce/composables/useHostElement";
import { useRovingTabindex } from "#ce/composables/useRovingTabindex";
import { getSlotContentByTagName } from "#ce/utils/slots";

defineOptions({ inheritAttrs: false });

const host = useHostElement();
const labels = shallowRef({});
const activeTab = shallowRef(null);
const tabsContainer = useTemplateRef("tabsContainer");
const panelsContainer = useTemplateRef("panelsContainer");
const { ariaLabel } = useAriaLabel(tabsContainer, {
  label: () => labels.value.label,
  labelledby: () => labels.value.labelledby,
});
useMutationObserver(host, refreshLabel, { attributes: true, attributeFilter: ["aria-label", "aria-labelledby"] });
onMounted(refreshLabel);

function refreshLabel() {
  labels.value = {
    label: host.getAttribute("aria-label") || undefined,
    labelledby: host.getAttribute("aria-labelledby") || undefined,
  };
}

const tabs = shallowRef([]);
const tabPanels = shallowRef([]);
const groupId = `sp-tab-group-${Math.random().toString(36).slice(2, 10)}`;
let nextTabId = 0;
let nextPanelId = 0;

const { getTabindex, handleKeydown } = useRovingTabindex({
  items: tabs,
  getActiveItem: () => tabs.value.find((tab) => tabPanelName(tab) === activeTab.value),
  activate: setActiveTab,
  focus: (tab) => tab.focus(),
  keys: {
    ArrowLeft: -1,
    ArrowRight: 1,
    Home: "first",
    End: "last",
  },
});

watch(activeTab, syncTabs, { flush: "sync" });

useEventListener(tabsContainer, "click", handleTabClick);
useEventListener(tabsContainer, "keydown", handleTabKeydown);
useEventListener(host, "sp:ce:ready", syncTabs, { capture: true });
onMounted(refreshSlots);

function refreshSlots() {
  tabs.value = getSlotContentByTagName(tabsContainer, "SP-TAB");
  tabPanels.value = getSlotContentByTagName(panelsContainer, "SP-TAB-PANEL");
  if (!tabs.value.some((tab) => tabPanelName(tab) === activeTab.value)) {
    setActiveTab(tabs.value.at(0));
  }
  syncTabs();
}

function eventTab(event) {
  return event.composedPath().find((element) => tabs.value.includes(element));
}

function handleTabClick(event) {
  const tab = eventTab(event);
  if (tab) {
    setActiveTab(tab);
  }
}

function handleTabKeydown(event) {
  const tab = eventTab(event);
  if (!tab) {
    return;
  }
  const direction = getComputedStyle(host).direction;
  const rtl = direction === "rtl";
  if (rtl && (event.key === "ArrowLeft" || event.key === "ArrowRight")) {
    handleKeydown(event, tab, event.key === "ArrowLeft" ? 1 : -1);
  } else {
    handleKeydown(event, tab);
  }
}

function syncTabs() {
  assignIds();
  updateAssociations();
  updateTabsActiveAttribute();
  updateTabPanelsActiveAttribute();
}

function assignIds() {
  tabs.value.forEach((tab) => {
    tab.id ||= `${groupId}-tab-${nextTabId++}`;
  });
  tabPanels.value.forEach((panel) => {
    panel.id ||= `${groupId}-panel-${nextPanelId++}`;
  });
}

function updateTabsActiveAttribute() {
  tabs.value.forEach((tab) => {
    tab.setAttribute("active", tabPanelName(tab) === activeTab.value);
    tab.setAttribute("role", "none");
    const panel = tabPanels.value.find((panel) => panelName(panel) === tabPanelName(tab));
    tab.setNavigationState?.({
      active: tabPanelName(tab) === activeTab.value,
      tabindex: getTabindex(tab),
      panel: panel ?? null,
    });
  });
}

function updateTabPanelsActiveAttribute() {
  tabPanels.value.forEach((panel) => {
    const isActive = panelName(panel) === activeTab.value;
    panel.setAttribute("active", isActive);
    panel.setActiveState?.(isActive);
    panel.hidden = !isActive;
  });
}

function updateAssociations() {
  tabPanels.value.forEach((panel) => {
    const tab = tabs.value.find((tab) => tabPanelName(tab) === panelName(panel));
    panel.setAttribute("role", "tabpanel");
    if (!panel.hasAttribute("tabindex")) {
      panel.setAttribute("tabindex", "0");
    }
    setAttributeOrRemove(panel, "aria-labelledby", tab?.id);
  });
}

function setAttributeOrRemove(element, name, value) {
  if (value) {
    element.setAttribute(name, value);
  } else {
    element.removeAttribute(name);
  }
}

function setActiveTab(tab) {
  activeTab.value = tabPanelName(tab);
}

// Slotted children upgrade after the group's initial activation; un-upgraded
// elements have no prop accessors yet, so fall back to the attribute.
function tabPanelName(tab) {
  return tab?.panel ?? tab?.getAttribute("panel");
}

function panelName(panel) {
  return panel?.name ?? panel?.getAttribute("name");
}
</script>

<style scoped lang="scss">
.sp-tab-group {
  display: flex;
  flex-direction: column;
  gap: var(--sp-comp-tab-group-gap, 1rem);
}

.sp-tab-group__nav {
  display: flex;
  gap: 1rem;
  border-bottom: 1px solid #ddd;
  padding-bottom: 0;
  align-items: center;
  justify-content: center;
}

.sp-tab-group__panels {
  display: flex;
  flex-direction: column;
}
</style>
